import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Heart,
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { useApp } from '@/pages/providers/AppProvider';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export function SavedJobsSection() {
  const navigate = useNavigate();
  const { jobs, applications, savedJobs, handleApplyJob, handleSaveJob } = useApp();

  // Get saved jobs with full data
  const savedJobsData = jobs?.filter(j => savedJobs?.includes(j.id)) || [];

  const hasApplied = (jobId: string) => {
    return applications?.some(a => a.jobId === jobId);
  };

  const handleApply = async (jobId: string) => {
    try {
      await handleApplyJob(jobId);
      toast.success('Application submitted successfully!');
    } catch (error: any) {
      if (error?.code === 'PROFILE_INCOMPLETE') {
        navigate('/jobseeker-dashboard/resume');
        return;
      }
      toast.error(error?.message || 'Failed to apply');
    }
  };

  const handleUnsave = (jobId: string) => {
    try {
      handleSaveJob(jobId);
      toast.success('Job removed from saved');
    } catch (error: any) {
      toast.error('Failed to remove job');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Saved Jobs</h2>
          <p className="text-gray-600 mt-1">Jobs you've bookmarked for later</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {savedJobsData.length} Saved
        </Badge>
      </div>

      {/* Saved Jobs Grid */}
      {savedJobsData.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No saved jobs</h3>
            <p className="text-gray-500 mb-4">Save jobs you're interested in to review later</p>
            <Button onClick={() => navigate('/jobseeker-dashboard/jobs')}>
              Browse Jobs
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {savedJobsData.map((job) => {
            const applied = hasApplied(job.id);

            return (
              <Card key={job.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-gray-400" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnsave(job.id)}
                    >
                      <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                    </Button>
                  </div>

                  {/* Job Info */}
                  <h3 className="font-semibold text-lg text-gray-900 mb-1 line-clamp-2">
                    {job.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{job.company}</p>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MapPin className="h-4 w-4" />
                      <span>{job.location || 'Remote'}</span>
                    </div>
                    {job.salary && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <DollarSign className="h-4 w-4" />
                        <span>{job.salary}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="h-4 w-4" />
                      <span>{job.type || 'Full-time'}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {applied ? (
                      <Button className="flex-1" disabled>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Applied
                      </Button>
                    ) : (
                      <Button className="flex-1" onClick={() => handleApply(job.id)}>
                        Apply Now
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate(`/jobs/${job.id}`)}>
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
